import React, { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import toast from 'react-hot-toast'
import logo from '../../images/sd-q-logo.png'

// ── Role-specific nav configs ─────────────────────────────────────────────────
// Admin: NO Finance, NO Trainers, NO Trainer Activity
const ADMIN_NAV = [
  { label: 'Dashboard', to: '/admin', icon: '🏠', exact: true },
  { label: 'Students', to: '/admin/students', icon: '👤' },
  {label: 'Certificates',to: '/admin/certificates',icon: '📜'},
  { label: 'Tests', to: '/admin/tests', icon: '📝' },
  { label: 'Test Results', to: '/admin/test-results', icon: '📊' },
  { label: 'Courses', to: '/admin/courses', icon: '📚' },
  { label: 'Batches', to: '/admin/batches', icon: '🗂️' },
  { label: 'Placements', to: '/admin/placements', icon: '🏢' },
  // { label: 'Analytics', to: '/admin/analytics', icon: '📈' },
  { label: 'Reports', to: '/admin/reports', icon: '📋' },
  { label: 'Attendance & Leave', to: '/admin/attendance', icon: '📅' },
  {
    label: 'Contact Submissions',
    to: '/admin/contact-submissions',
    icon: '📬',
  },
  { label: 'Website CMS', to: '/admin/cms', icon: '🌐' },
  { label: 'User Management', to: '/admin/user-management', icon: '👥' },

]

// Director: Full access to everything
const DIRECTOR_NAV = [
  { label: 'Dashboard', to: '/director', icon: '🏠', exact: true },
  { label: 'Students', to: '/director/students', icon: '👤' },
  { label: 'Certificates', to: '/director/certificates', icon: '📜' },
  { label: 'Tests', to: '/director/tests', icon: '📝' },
  { label: 'Test Results', to: '/director/test-results', icon: '📊' },
  { label: 'Trainers', to: '/director/trainers', icon: '🧑‍🏫' },
  { label: 'Trainer Activity', to: '/director/trainer-activity', icon: '📋' },
  { label: 'Syllabus', to: '/director/syllabus', icon: '📖' },
  { label: 'Courses', to: '/director/courses', icon: '📚' },
  { label: 'Batches', to: '/director/batches', icon: '🗂️' },
  { label: 'Placements', to: '/director/placements', icon: '🏢' },
  { label: 'Leave Approvals', to: '/director/leave-approvals', icon: '🏖️' },
  { label: 'Finance', to: '/director/finance', icon: '💰' },
  // { label: 'Analytics', to: '/director/analytics', icon: '📈' },
  { label: 'Reports', to: '/director/reports', icon: '📋' },
  {
    label: 'Contact Submissions',
    to: '/director/contact-submissions',
    icon: '📬',
  },
  { label: 'Website CMS', to: '/director/cms', icon: '🌐' },
  { label: 'User Management', to: '/director/user-management', icon: '👥' },
]

// Stakeholder: Same as Director (full access read mode)
const STAKEHOLDER_NAV = DIRECTOR_NAV.map((item) => ({
  ...item,
  to: item.to.replace('/director/', '/admin/').replace('/director', '/admin'),
}))

const TRAINER_NAV = [
  { label: 'Dashboard', to: '/trainer', icon: '🏠', exact: true },
  { label: 'My Students', to: '/trainer/students', icon: '👥' },
  { label: 'Attendance & Leave', to: '/trainer/attendance', icon: '📅' },
  { label: 'Syllabus', to: '/trainer/syllabus', icon: '📖' },
  { label: 'Tests', to: '/trainer/tests', icon: '📝' },
]

const STUDENT_NAV = [
  { label: 'Dashboard', to: '/student', icon: '🏠', exact: true },
  { label: 'Syllabus', to: '/student/syllabus', icon: '📖' },
  { label: 'Test Results', to: '/student/test-results', icon: '📊' },
]

const PLACEMENT_NAV = [
  { label: 'Students', to: '/placement-officer/students', icon: '👤' },
  { label: 'Certificates', to: '/director/certificates', icon: '📜' },
  { label: 'Placements', to: '/placement-officer/placements', icon: '🏢' },
  { label: 'Attendance & Leave', to: '/placement-officer/attendance', icon: '📅' },
]

const NAV_MAP = {
  admin: ADMIN_NAV,
  director: DIRECTOR_NAV,
  stakeholder: STAKEHOLDER_NAV,
  trainer: TRAINER_NAV,
  student: STUDENT_NAV,
  placementOfficer: PLACEMENT_NAV,
}

const ACCENT = {
  admin: '#4F46E5',
  director: '#7C3AED',
  stakeholder: '#8B5CF6',
  trainer: '#06B6D4',
  student: '#22C55E',
  placementOfficer: '#0284C7',
}
const TITLE = {
  admin: 'Admin Panel',
  director: 'Director Panel',
  stakeholder: 'Stakeholder Panel',
  trainer: 'Trainer Panel',
  student: 'Student Portal',
  placementOfficer: 'Placement Officer',
}

export default function DashboardLayout({ role: roleProp }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const role = roleProp || user?.role || 'student'
  const nav = NAV_MAP[role] || NAV_MAP.admin
  const accent = ACCENT[role] || ACCENT.admin

  const handleLogout = async () => {
    await logout()
    toast.success('Logged out')
    navigate('/login')
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F9FAFB' }}>
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.4)',
            zIndex: 40,
          }}
        />
      )}

      <aside
        className={open ? 'sidebar-open' : ''}
        style={{
          width: 248,
          background: '#fff',
          borderRight: '1px solid #E5E7EB',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: 0,
          zIndex: 50,
          overflowY: 'auto',
          transition: 'transform .25s',
        }}
      >
        <div
          style={{
            padding: '14px 16px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            position: 'sticky',
            top: 0,
            background: '#fff',
            zIndex: 1,
          }}
        >
          <div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src={logo}
                alt="SD Hub"
                style={{
                  height: 100,
                  width: 200,
                  objectFit: 'contain',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="close-sb-btn"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9CA3AF',
              display: 'none',
              padding: 4,
            }}
          >
            ✕
          </button>
        </div>

        <nav style={{ flex: 1, padding: '8px 6px' }}>
          {nav.map(({ label, to, icon, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={!!exact}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '7px 11px',
                borderRadius: 7,
                marginBottom: 2,
                fontSize: '.825rem',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? accent : '#4B5563',
                background: isActive ? `${accent}14` : 'transparent',
                transition: 'all .12s',
                textDecoration: 'none',
              })}
            >
              <span style={{ fontSize: '.9rem', lineHeight: 1, flexShrink: 0 }}>
                {icon}
              </span>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            </NavLink>
          ))}
        </nav>

        <div
          style={{
            padding: '12px 10px 14px',
            borderTop: '1px solid #E5E7EB',
            position: 'sticky',
            bottom: 0,
            background: '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 10,
              padding: '8px 10px',
              background: '#F9FAFB',
              borderRadius: 8,
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: `${accent}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                color: accent,
                fontSize: '.8rem',
                flexShrink: 0,
              }}
            >
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '.78rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.name}
              </div>
              <div
                style={{
                  fontSize: '.65rem',
                  color: '#9CA3AF',
                  textTransform: 'capitalize',
                }}
              >
                {user?.role}
              </div>
            </div>
          </div>
          <a
            href="/profile"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              width: '100%',
              padding: '7px',
              border: '1px solid #E5E7EB',
              borderRadius: 7,
              background: '#fff',
              fontSize: '.8rem',
              color: '#4B5563',
              marginBottom: 5,
              textDecoration: 'none',
            }}
          >
            👤 My Profile
          </a>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '7px',
              border: '1px solid #E5E7EB',
              borderRadius: 7,
              background: '#fff',
              cursor: 'pointer',
              fontSize: '.8rem',
              color: '#4B5563',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            🚪 Logout
          </button>
        </div>
      </aside>

      <div
        className="main-content"
        style={{
          marginLeft: 248,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <header
          style={{
            height: 58,
            background: '#fff',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            padding: '0 24px',
            gap: 12,
            position: 'sticky',
            top: 0,
            zIndex: 30,
          }}
        >
          <button
            onClick={() => setOpen(true)}
            className="hamburger-sb-btn"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.3rem',
              display: 'none',
              color: '#4B5563',
              padding: 4,
            }}
          >
            ☰
          </button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: '.78rem', color: '#9CA3AF' }}>
            {new Date().toLocaleDateString('en-IN', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </span>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: `${accent}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              color: accent,
              fontSize: '.82rem',
            }}
          >
            {user?.name?.[0]?.toUpperCase()}
          </div>
        </header>
        <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
          <Outlet />
        </main>
      </div>

      <style>{`
        @media(max-width:768px){
          .main-content{margin-left:0!important;}
          aside{transform:translateX(-100%)!important;}
          aside.sidebar-open{transform:translateX(0)!important;}
          .hamburger-sb-btn{display:flex!important;}
          .close-sb-btn{display:flex!important;}
          main{padding:14px!important;}
        }
        .table-wrap{overflow-x:auto;} table{min-width:560px;}
      `}</style>
    </div>
  )
}
